/**
 * Staff Orders Board — the post-payment fulfilment Kanban.
 *
 * Once an invoice is marked Paid on the Invoice Board it graduates here.
 * Four columns walk the order the rest of the way home:
 *
 *   Paid → Processed → In Transit → Delivered
 *
 * - Paid: customer has paid, order is queued for fulfilment.
 * - Processed: order is packed & ready, waiting for courier collection.
 * - In Transit: courier has it, out for delivery.
 * - Delivered: installer has confirmed receipt / job done.
 *
 * All transitions are drag-drop. Only In Transit → Delivered and moving into
 * In Transit are truly "final" actions; Paid ↔ Processed can go both ways
 * (mistake correction).
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Home, Package, Truck, PackageCheck, PoundSterling,
  Search, X, Clock, Trash2, Camera,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { formatApiError } from '@/utils/apiError';
import useHomePath from '@/hooks/useHomePath';
import XeroInvoiceBadge from '@/components/xero/XeroInvoiceBadge';
import { KanbanColumn, MoveToDropdown, KANBAN_PALETTE } from '@/components/kanban/KanbanShared';
import { OrderProfitBadge } from '@/components/kanban/OrderProfitBadge';

const BRAND = {
  probound:       { label: 'ProBound',       colour: '#2F5E96', ink: '#fff' },
  prodecor:       { label: 'ProDecor',       colour: '#D5AB09', ink: '#2B2101' },
  proepoxy:       { label: 'ProEpoxy',       colour: '#7A0B7A', ink: '#fff' },
  material_order: { label: 'Material Order', colour: '#1f4030', ink: '#fff' },
};

const COLUMNS = [
  { key: 'paid',       label: 'Paid',       icon: PoundSterling, statuses: ['paid', 'payment_confirmed'], colour: KANBAN_PALETTE.emerald },
  { key: 'processed',  label: 'Processed',  icon: Package,       statuses: ['processed'],                 colour: KANBAN_PALETTE.violet },
  { key: 'in_transit', label: 'In Transit', icon: Truck,         statuses: ['in_transit', 'dispatched'],  colour: KANBAN_PALETTE.amber },
  { key: 'delivered',  label: 'Delivered',  icon: PackageCheck,  statuses: ['delivered'],                 colour: KANBAN_PALETTE.green },
];

const columnForStatus = (s) => COLUMNS.find((c) => c.statuses.includes(s || ''));

// Each product has its own PATCH endpoint — mirror the pattern used by the Invoice Board.
const orderUpdateUrl = (order) =>
  `${API_URL}/api/staff/orders/${order.product}/${order.id}`;

const fmtGBP = (n) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 2 }).format(n || 0);

const daysAgo = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.floor((Date.now() - then) / (1000 * 60 * 60 * 24));
};

export default function OrdersProcessingBoard() {
  const navigate = useNavigate();
  const homePath = useHomePath();
  const { user } = useContext(AuthContext);
  const canDelete = ['admin', 'manager'].includes(user?.role);

  const [rowsByCol, setRowsByCol] = useState({ paid: [], processed: [], in_transit: [], delivered: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const combined = 'paid,payment_confirmed,processed,in_transit,dispatched,delivered';
      const res = await fetch(`${API_URL}/api/staff/orders?status=${combined}`, { credentials: 'include' });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Failed to load orders');
      const rows = await res.json();
      const buckets = { paid: [], processed: [], in_transit: [], delivered: [] };
      for (const r of rows) {
        const col = columnForStatus(r.status);
        if (col) buckets[col.key].push(r);
      }
      // Paid: FIFO by paid_at (oldest waiting on top).
      buckets.paid.sort((a, b) => (a.paid_at || a.payment_confirmed_at || '').localeCompare(b.paid_at || b.payment_confirmed_at || ''));
      buckets.processed.sort((a, b) => (a.processed_at || '').localeCompare(b.processed_at || ''));
      buckets.in_transit.sort((a, b) => (a.dispatched_at || a.in_transit_at || '').localeCompare(b.dispatched_at || b.in_transit_at || ''));
      buckets.delivered.sort((a, b) => (b.delivered_at || '').localeCompare(a.delivered_at || ''));
      setRowsByCol(buckets);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    if (!['admin', 'manager', 'team'].includes(user.role)) {
      navigate('/', { replace: true });
      return;
    }
    load();
  }, [user, navigate, load]);

  // Which target status does a drop into this column represent?
  const statusForColumnDrop = (colKey) => ({
    paid: 'paid',
    processed: 'processed',
    in_transit: 'in_transit',
    delivered: 'delivered',
  })[colKey];

  const moveOrder = async (order, targetCol) => {
    const currentCol = columnForStatus(order.status)?.key;
    if (!currentCol || currentCol === targetCol) return;
    const targetStatus = statusForColumnDrop(targetCol);
    // Optimistic move
    const snapshot = rowsByCol;
    setRowsByCol((cur) => {
      const next = { ...cur };
      next[currentCol] = next[currentCol].filter((o) => o.id !== order.id);
      next[targetCol] = [{ ...order, status: targetStatus }, ...next[targetCol]];
      return next;
    });
    try {
      const res = await fetch(orderUpdateUrl(order), {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: targetStatus }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Update failed');
      toast.success(`${order.reference || 'Order'} → ${COLUMNS.find(c => c.key === targetCol)?.title}`);
    } catch (e) {
      toast.error(e.message);
      setRowsByCol(snapshot);
    }
  };

  const softDelete = async (order, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    if (!canDelete) return;
    const currentCol = columnForStatus(order.status)?.key;
    const snapshot = rowsByCol;
    if (currentCol) {
      setRowsByCol((cur) => ({ ...cur, [currentCol]: cur[currentCol].filter((o) => o.id !== order.id) }));
    }
    try {
      const res = await fetch(`${API_URL}/api/staff/orders/${order.product}/${order.id}/delete`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Delete failed');
      toast(`${order.reference || 'Order'} moved to Recently Deleted`, { duration: 5000 });
    } catch (err) {
      toast.error(err.message);
      setRowsByCol(snapshot);
    }
  };

  const matchesSearch = useCallback((o) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (o.reference || '').toLowerCase().includes(q)
      || (o.customer_reference || '').toLowerCase().includes(q)
      || (o.installer_name || '').toLowerCase().includes(q)
      || (o.company_name || '').toLowerCase().includes(q)
      || (o.invoice_number || '').toLowerCase().includes(q);
  }, [search]);

  const totalOpen = useMemo(() => rowsByCol.paid.length + rowsByCol.processed.length + rowsByCol.in_transit.length, [rowsByCol]);

  const moveOptions = COLUMNS.map((c) => ({ key: c.key, label: c.label }));

  const renderCard = (order) => {
    const brand = BRAND[order.product] || {};
    const wait = daysAgo(order.paid_at || order.payment_confirmed_at || order.processed_at || order.dispatched_at || order.in_transit_at || order.created_at);
    return (
      <div
        key={order.id}
        onClick={() => navigate(order.product === 'material_order' ? `/material-orders/${order.id}` : `/${order.product}/orders/${order.id}`)}
        className="group relative bg-white border rounded-lg p-3 shadow-sm hover:shadow-md cursor-pointer transition"
        style={{ borderLeft: `4px solid ${brand.colour || '#94a3b8'}` }}
        data-testid={`orders-card-${order.id}`}
      >
        <div className="flex items-center gap-2 mb-1">
          <span
            className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
            style={{ background: brand.colour, color: brand.ink }}
          >{brand.label || order.product}</span>
          <span className="font-mono text-xs text-gray-700 truncate flex-1 min-w-0">
            {order.reference || order.id?.slice(0, 8)}
          </span>
          {canDelete && (
            <button
              type="button"
              onClick={(e) => softDelete(order, e)}
              className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded p-1 -mr-1 -my-1 transition"
              data-testid={`orders-delete-${order.id}`}
              title="Delete order"
              aria-label="Delete order"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="font-semibold text-sm text-gray-900 truncate">
          {order.customer_reference || order.installer_name || 'No reference'}
        </div>
        {order.company_name && (
          <div className="text-[11px] text-slate-500 truncate">{order.company_name}</div>
        )}
        <div className="flex items-center justify-between mt-2 gap-2">
          <span className="font-bold text-sm">{fmtGBP(order.total)}</span>
          {wait !== null && (
            <span className="text-[10px] text-gray-500 flex items-center gap-0.5">
              <Clock className="w-3 h-3" /> {wait}d
            </span>
          )}
        </div>
        <OrderProfitBadge profit={order.profit} orderId={order.id} />
        {order.packing && (
          <div className="mt-1 text-[10px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 inline-flex items-center gap-1" data-testid={`orders-packed-${order.id}`}>
            <Camera className="w-3 h-3" /> Packed by {order.packing.packed_by_name} · {order.packing.photo_ids?.length || 0} photo{order.packing.photo_ids?.length === 1 ? '' : 's'}
          </div>
        )}
        {order.invoice_number && (
          <div className="mt-1 text-[10px] font-mono text-indigo-700 truncate">Inv: {order.invoice_number}</div>
        )}
        {(order.xero_invoice_id || order.xero_last_error) && (
          <div className="mt-1.5">
            <XeroInvoiceBadge order={order} source={order.product} />
          </div>
        )}
        <div className="mt-2">
          <MoveToDropdown
            current={COLUMNS.find((c) => c.statuses.includes(order.status))?.key || order.status}
            options={moveOptions}
            onChange={(target) => moveOrder(order, target)}
            testId={`orders-move-${order.id}`}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #064e3b, #065f46)' }}>
        <div className="max-w-[1600px] mx-auto px-4 py-4 flex items-center gap-3 flex-wrap">
          <button
            onClick={() => navigate('/staff')}
            data-testid="orders-board-back"
            className="p-2 hover:bg-white/10 rounded-lg shrink-0"
            aria-label="Back to Staff Dashboard"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-xl font-bold flex items-center gap-2 truncate">
              <Truck className="w-5 h-5 shrink-0" /> Orders Board
            </h1>
            <p className="hidden sm:block text-white/70 text-xs">
              {totalOpen} in fulfilment · {rowsByCol.delivered.length} delivered · Use "Move to" on a card to advance
            </p>
          </div>
          <div className="relative shrink-0 hidden sm:block">
            <Search className="w-4 h-4 text-white/60 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search reference, customer, invoice…"
              data-testid="orders-board-search"
              className="pl-9 pr-8 py-2 bg-white/10 border border-white/20 rounded-lg text-sm placeholder-white/50 text-white w-56 md:w-72 focus:outline-none focus:bg-white/20 focus:border-white/40"
              aria-label="Search orders"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-white/60 hover:text-white"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Button
            onClick={() => navigate(homePath)}
            variant="outline"
            size="sm"
            className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0"
            aria-label="Home"
          >
            <Home className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Home</span>
          </Button>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 py-4">
        {loading ? (
          <Card className="p-10 text-center text-gray-500">Loading orders…</Card>
        ) : (
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-4 min-w-max">
              {COLUMNS.map((col) => {
                const rows = (rowsByCol[col.key] || []).filter(matchesSearch);
                return (
                  <KanbanColumn
                    key={col.key}
                    column={col}
                    count={rows.length}
                    testId={`orders-col-${col.key}`}
                    emptyLabel={search.trim() ? 'No matches' : 'No orders'}
                  >
                    {rows.map(renderCard)}
                  </KanbanColumn>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
